import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { Save, Trash2 } from 'lucide-react';

import { ApiError } from '../../api/client';
import { Fact, SectionLabel } from '../../components/detailPrimitives';
import RelativeTime from '../../components/RelativeTime';
import { useDeleteGroup, useGroupDetailQuery, useUpdateGroup } from '../../hooks/useGroupDetail';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '32px',
    maxWidth: '560px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '16px',
  },
  actions: {
    display: 'flex',
    columnGap: '8px',
    justifyContent: 'flex-end',
  },
  factGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    columnGap: '32px',
    rowGap: '20px',
  },
  danger: {
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: '#e4e4e7',
    paddingTop: '24px',
  },
  hint: {
    fontSize: '13px',
    color: '#71717a',
    marginTop: '4px',
  },
});

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const body = error.body as { error?: { message?: string } } | undefined;
    if (body?.error?.message) return body.error.message;
  }
  return error instanceof Error ? error.message : 'Unexpected error';
}

export default function GroupSettingsPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { groupId } = useParams<{ groupId: string }>();

  const query = useGroupDetailQuery(groupId);
  const update = useUpdateGroup(groupId);
  const remove = useDeleteGroup(groupId);

  const group = query.data?.migrationGroup;
  const memberCount = query.data?.rollup.migrationCount ?? 0;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // Seed the form from the server value once it arrives, and re-seed after a
  // save so the fields reflect what was actually stored (the server trims).
  useEffect(() => {
    if (group) {
      setName(group.name);
      setDescription(group.description ?? '');
    }
  }, [group]);

  if (!group) return null;

  return (
    <div className={styles.page}>
      <section>
        <SectionLabel>Details</SectionLabel>
        <form
          className={styles.form}
          onSubmit={(e) => {
            e.preventDefault();
            update.mutate({ name: name.trim(), description: description.trim() });
          }}
        >
          <Field label="Group name" required>
            <Input value={name} onChange={(_, d) => setName(d.value)} maxLength={128} />
          </Field>
          <Field label="Description">
            <Textarea
              value={description}
              onChange={(_, d) => setDescription(d.value)}
              resize="vertical"
              maxLength={512}
            />
          </Field>

          {update.isError && (
            <MessageBar intent="error">
              <MessageBarBody>
                <MessageBarTitle>Could not save</MessageBarTitle>
                {errorMessage(update.error)}
              </MessageBarBody>
            </MessageBar>
          )}
          {update.isSuccess && !update.isPending && (
            <MessageBar intent="success">
              <MessageBarBody>Saved.</MessageBarBody>
            </MessageBar>
          )}

          <div className={styles.actions}>
            <Button
              appearance="primary"
              type="submit"
              icon={<Save size={16} strokeWidth={1.5} />}
              disabled={!name.trim() || update.isPending}
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </section>

      <section>
        <SectionLabel>Provenance</SectionLabel>
        <div className={styles.factGrid}>
          <Fact label="Created">
            <RelativeTime iso={group.createdAt} /> by {group.createdBy}
          </Fact>
          <Fact label="Updated">
            <RelativeTime iso={group.updatedAt} /> by {group.updatedBy}
          </Fact>
        </div>
      </section>

      <section className={styles.danger}>
        <SectionLabel>Delete group</SectionLabel>
        {memberCount > 0 ? (
          // The server enforces this with a 409; saying so up front means the
          // operator learns the rule before hitting it, and knows the remedy is
          // to move the migrations rather than to find a force flag (there is
          // none, deliberately — FD-ADR-030 §5).
          <MessageBar intent="info">
            <MessageBarBody>
              <MessageBarTitle>This group is not empty</MessageBarTitle>
              It still contains {memberCount} migration{memberCount === 1 ? '' : 's'}. Remove them
              from the group before deleting it — deleting a group never changes the migrations
              inside it.
            </MessageBarBody>
          </MessageBar>
        ) : (
          <>
            <p className={styles.hint}>
              Deleting a group removes only the container. No migration is changed.
            </p>
            {remove.isError && (
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>Could not delete</MessageBarTitle>
                  {errorMessage(remove.error)}
                </MessageBarBody>
              </MessageBar>
            )}
            <div className={styles.actions} style={{ marginTop: '12px' }}>
              <Button
                appearance="secondary"
                icon={<Trash2 size={16} strokeWidth={1.5} />}
                disabled={remove.isPending}
                onClick={() => remove.mutate(undefined, { onSuccess: () => navigate('/groups') })}
              >
                {remove.isPending ? 'Deleting…' : 'Delete group'}
              </Button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
